import { useState, useEffect, useCallback } from "react";
import { Button, Table, Select, Input, Loader, Popup, Pagination } from "semantic-ui-react";
import { displayVisibilityOption, EVisibility, visibilityOptions } from "../../models/form";
import { toast } from "../..";
import "./ParameterManagementTable.css";
import {
    displayNetworkCodeOption,
    initParameterFilter,
    IParameter,
    IParameterFilter,
    NetworkCode,
    networkCodeOptions,
} from "../../models/parameter";
import { getParameters } from "../../services/parametersService";
import { ParameterModal } from "./ParameterModal";
import { DeleteParameterModal } from "./DeleteParameterModal";
import { HideParameterModal } from "./HideParameterModal";
import { PublishParameterModal } from "./PublishParameterModal";
import { timeDateFormat, localeString } from "../../helpers/commonHelper";

const NumberOfItemsToFetch = 5;

interface ITableProps {
    overview?: boolean;
    refreshTrigger?: boolean;
    setRefreshTrigger?: (value: boolean) => void;
    isDevelopment?: boolean;
}

export const ParameterManagementTable = ({
    overview,
    refreshTrigger,
    isDevelopment = false,
    setRefreshTrigger,
}: ITableProps) => {
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);

    const [parameterFilter, setParameterFilter] = useState<IParameterFilter>(initParameterFilter);

    const [tableData, setTableData] = useState<IParameter[]>([]);
    const [loading, setLoading] = useState(false);

    const [editParameterModalOpen, setEditParameterModalOpen] = useState(false);
    const [deleteParameterModalOpen, setDeleteParameterModalOpen] = useState(false);
    const [hideParameterModalOpen, setHideParameterModalOpen] = useState(false);
    const [publishParameterModalOpen, setPublishParameterModalOpen] = useState(false);
    const [selectedParameterGuid, setSelectedParameterGuid] = useState<string>();

    const fetchParameters = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getParameters(
                parameterFilter,
                page,
                NumberOfItemsToFetch,
                isDevelopment,
                false
            );
            setTableData(res.result);
            setTotalRows(res.totalRows);
        } catch {
            toast("Error fetching parameters", false, 1000);
        }
        setLoading(false);
    }, [parameterFilter, refreshTrigger, page, isDevelopment]);

    useEffect(() => {
        fetchParameters();
    }, [fetchParameters]);

    useEffect(() => {
        setPage(1);
    }, [parameterFilter]);

    return (
        <>
            <Table fixed className="forms-overview" columns={overview ? 7 : 9}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            <Input
                                fluid
                                value={parameterFilter.name}
                                onChange={e =>
                                    setParameterFilter({
                                        ...parameterFilter,
                                        name: e.target.value,
                                    })
                                }
                                icon="search"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Select
                                clearable
                                selectOnBlur={false}
                                className="filter-selection"
                                options={networkCodeOptions}
                                value={parameterFilter.networkCode}
                                onChange={(_e, data) =>
                                    setParameterFilter({
                                        ...parameterFilter,
                                        networkCode:
                                            data.value === ""
                                                ? undefined
                                                : (data.value as NetworkCode),
                                    })
                                }
                                text={
                                    parameterFilter.networkCode !== undefined
                                        ? displayNetworkCodeOption(parameterFilter.networkCode)
                                        : "Network code"
                                }
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Select
                                clearable
                                selectOnBlur={false}
                                className="filter-selection"
                                options={visibilityOptions}
                                value={parameterFilter.visibility}
                                onChange={(_e, data) =>
                                    setParameterFilter({
                                        ...parameterFilter,
                                        visibility:
                                            data.value === ""
                                                ? undefined
                                                : (data.value as EVisibility),
                                    })
                                }
                                text={
                                    parameterFilter.visibility !== undefined
                                        ? displayVisibilityOption(parameterFilter.visibility)
                                        : "Visibility"
                                }
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Button
                                primary
                                content="Clear filters"
                                onClick={() => {
                                    setParameterFilter(initParameterFilter);
                                }}
                            />
                        </Table.HeaderCell>
                    </Table.Row>
                    <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Network code</Table.HeaderCell>
                        <Table.HeaderCell>Visibility</Table.HeaderCell>
                        <Table.HeaderCell>Date created</Table.HeaderCell>
                        <Table.HeaderCell>Deadline</Table.HeaderCell>
                        <Table.HeaderCell></Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {tableData?.map(element => {
                        return (
                            <Table.Row key={element.guid}>
                                <Table.Cell>{element.name}</Table.Cell>
                                <Table.Cell>
                                    {displayNetworkCodeOption(element.networkCode)}
                                </Table.Cell>
                                <Table.Cell>
                                    {displayVisibilityOption(element.visibility)}
                                </Table.Cell>
                                <Table.Cell>
                                    {element.timeCreated
                                        ? new Date(element.timeCreated).toLocaleString(
                                              localeString,
                                              timeDateFormat
                                          )
                                        : ""}
                                </Table.Cell>
                                <Table.Cell>
                                    {element.deadline
                                        ? new Date(element.deadline).toLocaleString(
                                              localeString,
                                              timeDateFormat
                                          )
                                        : ""}
                                </Table.Cell>

                                <Table.Cell textAlign="right">
                                    <Popup
                                        content="Edit"
                                        trigger={
                                            <Button
                                                primary
                                                icon="edit"
                                                onClick={() => {
                                                    setSelectedParameterGuid(element.guid);
                                                    setEditParameterModalOpen(true);
                                                }}
                                            />
                                        }
                                    />
                                    {isDevelopment ? (
                                        <Popup
                                            content="Publish"
                                            trigger={
                                                <Button
                                                    primary
                                                    icon="cloud upload"
                                                    onClick={() => {
                                                        setSelectedParameterGuid(element.guid);
                                                        setPublishParameterModalOpen(true);
                                                    }}
                                                />
                                            }
                                        />
                                    ) : (
                                        <Popup
                                            content={
                                                element.hidden
                                                    ? "Unhide parameter"
                                                    : "Hide parameter"
                                            }
                                            trigger={
                                                <Button
                                                    primary
                                                    icon={element.hidden ? "eye" : "eye slash"}
                                                    onClick={() => {
                                                        setSelectedParameterGuid(element.guid);
                                                        setHideParameterModalOpen(true);
                                                    }}
                                                />
                                            }
                                        />
                                    )}
                                    <Popup
                                        content="Delete"
                                        trigger={
                                            <Button
                                                primary
                                                icon="trash"
                                                onClick={() => {
                                                    setSelectedParameterGuid(element.guid);
                                                    setDeleteParameterModalOpen(true);
                                                }}
                                            />
                                        }
                                    />
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
            <Loader active={loading} inline="centered" />
            <div className="parameters-pagination-container">
                <Pagination
                    activePage={page}
                    totalPages={Math.ceil(totalRows / NumberOfItemsToFetch)}
                    onPageChange={(_event, data) => setPage(data.activePage as number)}
                />
            </div>

            {editParameterModalOpen && (
                <ParameterModal
                    modalOpen={editParameterModalOpen}
                    closeModal={() => setEditParameterModalOpen(false)}
                    parameterGuid={selectedParameterGuid}
                    refreshTrigger={fetchParameters}
                    isDevelopment={isDevelopment}
                />
            )}
            {deleteParameterModalOpen && (
                <DeleteParameterModal
                    deleteParameterModalOpen={deleteParameterModalOpen}
                    closeDeleteParameterModal={() => setDeleteParameterModalOpen(false)}
                    parameterToDelete={
                        tableData.find(p => p.guid === selectedParameterGuid) as IParameter
                    }
                    refreshParameters={fetchParameters}
                />
            )}
            {hideParameterModalOpen && (
                <HideParameterModal
                    hideParameterModalOpen={hideParameterModalOpen}
                    closeHideParameterModal={() => setHideParameterModalOpen(false)}
                    parameterToHide={
                        tableData.find(p => p.guid === selectedParameterGuid) as IParameter
                    }
                    refreshParameters={fetchParameters}
                />
            )}
            {publishParameterModalOpen && (
                <PublishParameterModal
                    publishParameterModalOpen={publishParameterModalOpen}
                    closePublishParameterModal={() => setPublishParameterModalOpen(false)}
                    parameterToPublish={
                        tableData.find(p => p.guid === selectedParameterGuid) as IParameter
                    }
                    refreshParameters={() => {
                        fetchParameters();
                        setRefreshTrigger && setRefreshTrigger(!refreshTrigger);
                    }}
                />
            )}
        </>
    );
};
